import React, { useState } from 'react';
import {
  XMarkIcon,
  TrashIcon,
  CheckIcon,
  UserPlusIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import Table from '../common/Table';
import toast from 'react-hot-toast';

import { useMutation, useQueryClient } from 'react-query';
import { disputesAPI } from '../../services/api';
import { useFetch } from '../../hooks/useCallApi';

export default function LuponMembersManagement({ dispute, isOpen, onClose }) {
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState('add');

  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteName, setDeleteName] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [search, setSearch] = useState('');

  const handlePageChange = (page, newPerPage) => {
    if (newPerPage && newPerPage !== perPage) {
      setPerPage(newPerPage);
      setCurrentPage(1);
    } else {
      setCurrentPage(page);
    }
  };

  const [resPagination, setResPagination] = useState({
    currentPage: 1,
    perPage: 5,
  });

  const { data: luponData, isLoading: luponLoading } = useFetch(
    ['lupon-members', dispute?.id],
    () => disputesAPI.getLupon(dispute?.id),
    {
      enabled: !!dispute?.id,
    }
  );
  const membersList = luponData?.result || [];
  const metaLupon = luponData?.meta;
  // const paginatedMembers = membersList.slice(
  //   (luponPagination.currentPage - 1) * luponPagination.perPage,
  //   luponPagination.currentPage * luponPagination.perPage
  // )

  const { data: luponResData, isLoading: resLoading } = useFetch(
    ['lupon-members-residents'],
    disputesAPI.getLuponRes
  );
  const membersResList = luponResData?.result || [];
  console.log('memembersResListmem', membersResList);

  const luponResidentIds = new Set(membersList.map((m) => m.resident?.id));
  const availableResidents = membersResList.filter(
    (r) => !luponResidentIds.has(r.id)
  );
  const filteredResidents = availableResidents.filter((m) =>
    m.full_name?.toLowerCase().includes(search.toLowerCase())
  );
  const paginatedResidents = filteredResidents.slice(
    (resPagination.currentPage - 1) * resPagination.perPage,
    resPagination.currentPage * resPagination.perPage
  );

  const addMutation = useMutation(
    ({ disputeId, data }) => disputesAPI.addLupon(disputeId, data),
    {
      onSuccess: () => {
        toast.success('Lupon member added successfully');
        queryClient.invalidateQueries(['lupon-members', dispute?.id]);
      },
      onError: (error) => {
        console.error('Add Lupon Error:', error);
        toast.error('Failed to add member');
      },
    }
  );
  const handleAddFromResident = (resident) => {
    addMutation.mutate({
      disputeId: dispute.id,
      data: { resident_ids: [resident.id] },
    });
  };

  const deleteMutation = useMutation((id) => disputesAPI.deleteLupon(id), {
    onSuccess: () => {
      toast.success('Lupon member deleted successfully');
      queryClient.invalidateQueries(['lupon-members']);
      setShowDeleteModal(false);
      setDeleteId(null);
    },
    onError: (error) => {
      console.error('Delete Lupon Error:', error);
      toast.error('Failed to delete member');
    },
  });

  const handleDelete = () => {
    if (!deleteId) return;
    deleteMutation.mutate(deleteId);
  };

  const residentColumns = [
    {
      key: 'full_name',
      label: <span className='text-xs font-medium'>RESIDENT NAME</span>,
      render: (_, item) => (
        <span className='text-gray-800 text-sm'>{item.full_name}</span>
      ),
    },
    {
      key: 'contact_number',
      label: <span className='text-xs font-medium'>CONTACT</span>,
      render: (_, item) => (
        <span className='text-gray-600 text-sm'>
          {item.contact_number || '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: <span className='text-xs font-medium'>ADD</span>,
      render: (_, item) => (
        <button
          onClick={() => handleAddFromResident(item)}
          disabled={addMutation.isLoading}
          className='flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs font-medium disabled:opacity-50'
        >
          <UserPlusIcon className='w-4 h-4' />
          Add
        </button>
      ),
    },
  ];

  const luponColumns = [
    {
      key: 'name',
      label: <span className='text-xs font-medium'>MEMBER NAME</span>,
      render: (_, item) =>
        editingId === item.id ? (
          <input
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            className='w-full border border-blue-400 rounded px-2 py-1 text-sm'
          />
        ) : (
          <span className='text-gray-900 text-sm'>
            {item.resident?.full_name}
          </span>
        ),
    },
    {
      key: 'contact_no',
      label: <span className='text-xs font-medium'>CONTACT NUMBER</span>,
      render: (_, item) =>
        editingId === item.id ? (
          <input
            value={editPhone}
            onChange={(e) => setEditPhone(e.target.value)}
            className='w-full border border-blue-400 rounded px-2 py-1 text-sm'
          />
        ) : (
          <span className='text-gray-900 text-sm'>
            {item.resident?.contact_number || '—'}
          </span>
        ),
    },
    {
      key: 'actions',
      label: <span className='text-xs font-medium'>ACTIONS</span>,
      render: (_, item) => (
        <div className='flex gap-3'>
          {editingId === item.id ? (
            <>
              <button
                onClick={() => handleUpdate(item.id)}
                className='text-green-600 hover:text-green-700'
              >
                <CheckIcon className='w-5 h-5' />
              </button>
              <button
                onClick={() => setEditingId(null)}
                className='text-red-500 hover:text-red-600'
              >
                <XMarkIcon className='w-5 h-5' />
              </button>
            </>
          ) : (
            <button
              onClick={() => {
                setDeleteId(item.id);
                setDeleteName(item.resident?.full_name || item.name);
                setShowDeleteModal(true);
              }}
              className='text-red-500 hover:text-red-600'
            >
              <TrashIcon className='w-4 h-4' />
            </button>
          )}
        </div>
      ),
    },
  ];

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 z-[60] flex justify-center overflow-y-auto py-10'>
      <div className='fixed inset-0 bg-black/50' onClick={onClose} />

      <div className='relative bg-white rounded-xl w-full max-w-4xl shadow-2xl p-6 my-auto'>
        <div className='flex justify-between items-center mb-6 border-b pb-4'>
          <h2 className='text-lg font-bold text-gray-800'>
            Lupon Members Management
          </h2>
          <button onClick={onClose}>
            <XMarkIcon className='w-6 h-6 text-gray-500 hover:text-gray-700' />
          </button>
        </div>

        <div className='flex gap-2 mb-5'>
          <button
            onClick={() => setActiveTab('add')}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'add'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Add Members
          </button>
          <button
            onClick={() => setActiveTab('members')}
            className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'members'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Lupon Members
            <span
              className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                activeTab === 'members'
                  ? 'bg-white/20 text-white'
                  : 'bg-gray-300 text-gray-600'
              }`}
            >
              {metaLupon.total}
            </span>
          </button>
        </div>

        {activeTab === 'add' && (
          <div>
            <div className='flex items-center justify-between mb-3'>
              <h3 className='text-sm font-semibold text-gray-700'>
                Resident List
              </h3>
              <span className='text-xs text-gray-400'>
                {filteredResidents.length} available
              </span>
            </div>

            <div className='relative mb-3'>
              <MagnifyingGlassIcon className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400' />
              <input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setResPagination((p) => ({ ...p, currentPage: 1 }));
                }}
                placeholder='Search residents...'
                className='w-full border border-gray-300 rounded-lg pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300'
              />
            </div>

            <div className='border border-gray-200 rounded-lg overflow-hidden'>
              <Table
                noContainer={true}
                columns={residentColumns}
                data={paginatedResidents}
                getRowKey={(item) => item.id}
                pagination={{
                  currentPage: resPagination.currentPage,
                  lastPage: Math.ceil(
                    filteredResidents.length / resPagination.perPage
                  ),
                  perPage: resPagination.perPage,
                  total: filteredResidents.length,
                }}
                onPageChange={(page) =>
                  setResPagination((prev) => ({ ...prev, currentPage: page }))
                }
                paginationProps={{
                  showSizeSelector: true,
                  isLoading: resLoading,
                  onPerPageChange: (size) =>
                    setResPagination({ currentPage: 1, perPage: size }),
                }}
              />
            </div>
          </div>
        )}

        {activeTab === 'members' && (
          <div>
            <div className='flex items-center justify-between mb-3'>
              <h3 className='text-sm font-semibold text-gray-700'>
                Current Lupon Members
              </h3>
              <span className='text-xs text-gray-400'>
                {membersList.length} member{membersList.length !== 1 ? 's' : ''}
              </span>
            </div>

            <div className='border border-gray-200 rounded-lg max-h-72 overflow-y-auto'>
              <Table
                noContainer={true}
                columns={luponColumns}
                data={membersList}
                getRowKey={(item) => item.id}
                pagination={{
                  currentPage: metaLupon.current_page,
                  lastPage: metaLupon.last_page,
                  perPage: metaLupon.per_page,
                  total: metaLupon.total,
                }}
                onPageChange={handlePageChange}
                paginationProps={{
                  showSizeSelector: true,
                  isLoading: luponLoading,
                  onPerPageChange: (size) =>
                    setLuponPagination({ currentPage: 1, perPage: size }),
                }}
              />
            </div>
          </div>
        )}
      </div>

      {showDeleteModal && (
        <div className='fixed inset-0 z-[70] flex items-center justify-center bg-black/50'>
          <div className='bg-white rounded-lg shadow-lg p-6 w-full max-w-md text-sm'>
            <h3 className='text-lg font-bold mb-4'>Remove Lupon Member</h3>
            <p className='mb-6'>
              Are you sure you want to remove <strong>{deleteName}</strong> from
              the Lupon?
            </p>
            <div className='flex justify-end gap-3'>
              <button
                onClick={() => setShowDeleteModal(false)}
                className='px-4 py-2 bg-gray-200 rounded hover:bg-gray-300'
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isLoading}
                className='px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50'
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
